import { mapAPIError } from './error';
import type { APIError } from './error';
import { convert, Instant } from '@js-joda/core';
import { enableRemoteSuperuserHeader, requestIdHeader } from '@thinkalpha/common/contracts/x-headers.js';
import { replacer, reviver } from '@thinkalpha/common/serialization/serializer.js';
import { replacer as legacyReplacer, reviver as legacyReviver } from '@thinkalpha/ui-core/serializer';
import axios from 'axios';
import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { isEqual } from 'es-toolkit';
import type { Store } from 'redux';
import {
    distinctUntilChanged,
    filter,
    firstValueFrom,
    from,
    map,
    of,
    ReplaySubject,
    share,
    startWith,
    switchMap,
    timeout,
    timer,
} from 'rxjs';
import type { Observable } from 'rxjs';
import { appConfig } from 'src/lib/config';
import { rxifyAxios } from 'src/lib/http';
import type { ApiAuthService, RetryableAxiosRequestConfig } from 'src/services/ApiAuthService';
import type { SessionService } from 'src/services/SessionService';
import { container } from 'src/StaticContainer';
import type { AuthState } from 'src/store/reducers/auth';
import type { ClientCredentials } from 'src/store/types';
import { v4 as uuidv4 } from 'uuid';

axiosRetry(axios);

const log = container.get('Logger').getSubLogger({ name: 'api' });

export const SUPERUSER_ACCESS_EXPIRATION_KEY = 'superuser_access_expiration';

type RootState = {
    auth?: AuthState;
    ui: { superuserAccessEnabledUntil?: Instant };
};
const states$ = new ReplaySubject<Observable<RootState>>(1);
const state$ = states$.pipe(switchMap((stream) => stream));

export function setStoreForApi(storeIn: Store<RootState>) {
    const state$ = from(storeIn);
    states$.next(state$);
}

const accessToken$ = state$.pipe(
    map((x) => x.auth?.masqAccessToken ?? x.auth?.accessToken ?? x.auth?.clientCredentials),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    share({ connector: () => new ReplaySubject(1), resetOnRefCountZero: false }),
);

// the first (and every) time this pipe above emits, it will be a valid unexpired key
const validToken$ = accessToken$.pipe(
    filter((token) => token !== undefined),
    filter((token) => token === null || 'clientId' in token || !token.expires || token.expires.isAfter(Instant.now())),
    // ^^^ token is anonymous || token doesn't expire || token is not expired (expires after right now)
    map((token) => (token ? ('clientId' in token ? token : token.token) : null)),
    timeout(10000),
);

let sessionServiceForApi: SessionService | undefined;

/**
 * Electron renderers receive authentication outside the Redux proxy store.
 * The access token remains a live SessionService signal owned by main.
 */
export function setSessionServiceForApi(sessionService: SessionService): void {
    sessionServiceForApi = sessionService;
}

async function getUsableAccessToken(): Promise<string | ClientCredentials | null> {
    if (sessionServiceForApi) return sessionServiceForApi.accessToken.value;
    return firstValueFrom(validToken$);
}

const isSuperuser$ = state$.pipe(
    map((x) => x.ui.superuserAccessEnabledUntil),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    switchMap((x) =>
        !x || x.isBefore(Instant.now())
            ? of(false)
            : timer(convert(x).toDate()).pipe(
                  map(() => false),
                  startWith(true),
              ),
    ),
    share({ connector: () => new ReplaySubject(1), resetOnRefCountZero: false }),
);

async function getIsSuperuserModeEnabled(): Promise<boolean> {
    return firstValueFrom(isSuperuser$);
}

// Allow storybook to short-circuit the store credentials dependency (which won't be satisfied in storybook)
let fixedAuthorizationHeader: { Authorization: string } | undefined;
export function __setStorybookCredentials(credentials: { CLIENT_ID: string; CLIENT_SECRET: string }) {
    fixedAuthorizationHeader = {
        Authorization: `Basic ${btoa(`${credentials.CLIENT_ID}:${credentials.CLIENT_SECRET}`)}`,
    };
}

// Allow storybook to short-circuit the store admin-mode dependency (which won't be satisfied in storybook)
let fixedAdminMode: boolean | undefined;
export function __setStorybookAdminMode(enabled: boolean) {
    fixedAdminMode = enabled;
}

type RequestInterceptorFunc = (req: InternalAxiosRequestConfig) => Promise<InternalAxiosRequestConfig>;
type ResponseInterceptorFunc = (req: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>;

type RequestInterceptor = [onFulfilled: RequestInterceptorFunc, onRejected?: (e: Error) => Promise<never>];
type ResponseInterceptor = [
    onFulfilled: ResponseInterceptorFunc,
    onRejected?: (e: Error) => Promise<AxiosResponse> | Promise<never>,
];

const addAuthorizationHeadersInterceptor: RequestInterceptor = [
    async (req: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
        let authorizationHeader;
        if (fixedAuthorizationHeader) {
            authorizationHeader = fixedAuthorizationHeader;
        } else {
            const token = await getUsableAccessToken();
            if (token) {
                if (typeof token === 'string') {
                    authorizationHeader = {
                        Authorization: `Bearer ${token}`,
                    };
                } else if ('clientId' in token) {
                    req.auth = {
                        username: token.clientId,
                        password: token.clientSecret,
                    };
                }
            }
        }

        if (!req.headers.has('Content-Type')) req.headers['Content-Type'] = 'application/json';
        for (const header in authorizationHeader) {
            // don't overwrite the Authorization header if it already exists
            if (req.headers[header]) continue;
            req.headers[header] = (authorizationHeader as any)[header];
        }

        return req;
    },
];

const addHeaderAdminInterceptor: RequestInterceptor = [
    async (req: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
        let isSuperuserPrivilegeEnabled = false;
        if (fixedAdminMode !== undefined) {
            isSuperuserPrivilegeEnabled = fixedAdminMode;
        } else if (await getIsSuperuserModeEnabled()) {
            isSuperuserPrivilegeEnabled = true;
        }

        req.headers[enableRemoteSuperuserHeader] = isSuperuserPrivilegeEnabled.toString();

        return req;
    },
];

export const addRequestIdInterceptor: RequestInterceptor = [
    async (req: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
        if (!req.headers[requestIdHeader]) {
            req.headers[requestIdHeader] = uuidv4();
        }
        return req;
    },
];

const handleAxiosError = (error: AxiosError<APIError>) => {
    // Prefer server's response (authoritative, matches server logs), fall back to what we sent
    let requestId: string | undefined;
    try {
        if (typeof error.response?.data?.requestId === 'string') {
            requestId = error.response.data.requestId;
        } else if (typeof error.response?.headers?.[requestIdHeader] === 'string') {
            requestId = error.response.headers[requestIdHeader];
        } else if (typeof error.config?.headers?.[requestIdHeader] === 'string') {
            requestId = error.config.headers[requestIdHeader];
        }
    } catch (e) {
        log.error('Failed to extract requestId from error', { error: e });
    }

    const meta = {
        error,
        requestId,
        url: error.config?.url,
        method: error.config?.method,
        params: error.config?.params,
    };

    // Don't log network errors or cancellations
    if (error.code === 'ERR_CANCELED' || error.code === 'ERR_NETWORK') {
        return Promise.reject(error);
    }

    if (!requestId || !error.status) {
        log.error('Unexpected API Error', meta);
    } else if (error.status >= 400 && error.status < 500) {
        log.error('Request Error', meta);
    } else if (error.status >= 500) {
        log.warn('Server Error', meta);
    } else {
        log.error('API Error', meta);
    }

    return Promise.reject(error);
};

const isAxiosError = (err: Error): err is AxiosError => (err as any).isAxiosError === true;

const detectUnauthorizedInterceptor: ResponseInterceptor = [
    // Success handler - pass through
    async (res: AxiosResponse): Promise<AxiosResponse> => {
        return res;
    },
    // Error handler - delegate 401s to ApiAuthService
    async (err: Error) => {
        if (!isAxiosError(err)) {
            throw err;
        }

        // Try to get ApiAuthService
        let apiAuthService: ApiAuthService | null = null;
        try {
            if ((container as any).isBound('ApiAuthService')) {
                apiAuthService = (container as any).get('ApiAuthService');
            }
        } catch (error) {
            // Service not available yet during bootstrap
            if (TRACE) {
                log.trace('ApiAuthService not available', { error });
            }
        }

        // If we have the service and this is a 401, let it handle it
        if (apiAuthService && err.response?.status === 401) {
            return apiAuthService.handleUnauthorizedRequest(err);
        }

        // Otherwise, just propagate the error
        throw err;
    },
];

/**
 * Creates an axios instance for unauthenticated (public) API calls.
 * Same plain-JSON serialization as `createInstance` with `useLegacySerialization: false`,
 * but skips auth, admin, and unauthorized-detection interceptors.
 * Use this for signup and other public endpoints that don't require a logged-in user.
 */
export function createPublicInstance(config?: AxiosRequestConfig): AxiosInstance {
    const inst = axios.create({
        baseURL: appConfig.api,
        ...config,
        transformRequest: [
            (payload, headers) => {
                if (typeof payload === 'object') {
                    try {
                        const json = JSON.stringify(payload, undefined, 2);
                        headers['Content-Type'] = 'application/json';
                        return json;
                    } catch (e) {
                        return e;
                    }
                } else {
                    return payload;
                }
            },
            ...(!config?.transformRequest
                ? []
                : Array.isArray(config.transformRequest)
                  ? config.transformRequest
                  : [config.transformRequest]),
        ],
        transformResponse: [
            (resp) => {
                return resp && JSON.parse(resp);
            },
            ...(!config?.transformResponse
                ? []
                : Array.isArray(config.transformResponse)
                  ? config.transformResponse
                  : [config.transformResponse]),
        ],
        paramsSerializer: { indexes: false },
    });

    inst.interceptors.request.use(...addRequestIdInterceptor);

    inst.interceptors.response.use(
        (response) => response,
        (error: AxiosError<APIError>) => handleAxiosError(error),
    );

    return inst;
}

/**
 * Creates an axios instance that uses the serializer
 * from @thinkalpha/common/serialization/serializer.js
 * @param config - Axios configuration
 * @param options - Additional options
 * @param options.useLegacySerialization - Whether to use legacy replacer/reviver (default: true). Set to false when using zod schemas for serialization.
 * @param options.validateStatus - Optional custom axios validateStatus handler for callers that need non-2xx responses treated as resolved.
 */
export function createInstance(
    config?: AxiosRequestConfig,
    {
        useLegacySerialization = true,
        validateStatus,
    }: {
        useLegacySerialization?: boolean;
        validateStatus?: AxiosRequestConfig['validateStatus'];
    } = {},
): AxiosInstance {
    const inst = axios.create({
        baseURL: appConfig.api,
        ...config,
        ...(validateStatus ? { validateStatus } : {}),
        transformRequest: [
            (payload, headers) => {
                if (typeof payload === 'object') {
                    try {
                        const json = useLegacySerialization
                            ? JSON.stringify(payload, replacer, 2)
                            : JSON.stringify(payload, undefined, 2);
                        headers['Content-Type'] = 'application/json';
                        return json;
                    } catch (e) {
                        return e;
                    }
                } else {
                    return payload;
                }
            },
            ...(!config?.transformRequest
                ? []
                : Array.isArray(config.transformRequest)
                  ? config.transformRequest
                  : [config.transformRequest]),
        ],
        transformResponse: [
            (resp) => {
                return resp && (useLegacySerialization ? JSON.parse(resp, reviver) : JSON.parse(resp));
            },
            ...(!config?.transformResponse
                ? []
                : Array.isArray(config.transformResponse)
                  ? config.transformResponse
                  : [config.transformResponse]),
        ],
        paramsSerializer: { indexes: false },
    });

    // Add axios instance reference to all requests so ApiAuthService can use it for retries
    inst.interceptors.request.use((config) => {
        const retryableConfig: RetryableAxiosRequestConfig = config;
        retryableConfig._axiosInstance = inst;
        return retryableConfig;
    });

    inst.interceptors.request.use(...addRequestIdInterceptor);
    inst.interceptors.request.use(...addAuthorizationHeadersInterceptor);
    inst.interceptors.request.use(...addHeaderAdminInterceptor);
    inst.interceptors.response.use(...detectUnauthorizedInterceptor);

    inst.interceptors.response.use(
        (response) => response,
        (error: AxiosError<APIError>) => handleAxiosError(error),
    );

    return inst;
}

/**
 *
 * Creates an axios legacy instance that uses the deprecated platform-ui
 * replacer and reviver
 *
 * @deprecated Use createInstance instead
 */
export function createLegacyInstance(config?: AxiosRequestConfig): AxiosInstance {
    const inst = axios.create({
        baseURL: appConfig.api,
        ...config,
        transformRequest: [
            (payload, headers) => {
                if (typeof payload === 'object') {
                    try {
                        const json = JSON.stringify(payload, legacyReplacer, 2);
                        headers['Content-Type'] = 'application/json';
                        return json;
                    } catch (e) {
                        return e;
                    }
                } else {
                    return payload;
                }
            },
            ...(!config?.transformRequest
                ? []
                : Array.isArray(config.transformRequest)
                  ? config.transformRequest
                  : [config.transformRequest]),
        ],
        transformResponse: [
            (resp) => {
                return resp && JSON.parse(resp, legacyReviver);
            },
            ...(!config?.transformResponse
                ? []
                : Array.isArray(config.transformResponse)
                  ? config.transformResponse
                  : [config.transformResponse]),
        ],
        paramsSerializer: { indexes: false },
    });

    // Add axios instance reference to all requests so ApiAuthService can use it for retries
    inst.interceptors.request.use((config) => {
        const retryableConfig: RetryableAxiosRequestConfig = config;
        retryableConfig._axiosInstance = inst;
        return retryableConfig;
    });

    inst.interceptors.request.use(...addRequestIdInterceptor);
    inst.interceptors.request.use(...addAuthorizationHeadersInterceptor);
    inst.interceptors.request.use(...addHeaderAdminInterceptor);
    inst.interceptors.response.use(...detectUnauthorizedInterceptor);

    inst.interceptors.response.use(
        (response) => response,
        (error: AxiosError<APIError>) => handleAxiosError(error),
    );

    return inst;
}

const inst = createLegacyInstance();
const rxInst = rxifyAxios(inst, mapAPIError());

const locatesInst = createLegacyInstance({ baseURL: appConfig.locatesApi });
const tradingInst = createInstance({ baseURL: appConfig.tradingApi });

// Workspace-scoped axios pair. The legacy `inst` above uses the deprecated
// `@thinkalpha/ui-core` reviver/replacer; that path is overly greedy about
// Period-shaped strings (it matches `P3M` as a Duration and crashes on
// `Duration.parse(...)`). Workspace payloads now carry AlphaEye's Period
// `chartLookback`, so this dedicated instance routes /workspaces/* traffic
// through `createInstance()` (which uses the modern `@thinkalpha/common`
// reviver — try/caught and Period-tolerant). Other consumers of `api` /
// `rxapi` stay on the legacy instance unchanged.
const workspaceInst = createInstance();
const workspaceRxInst = rxifyAxios(workspaceInst, mapAPIError());

export {
    inst as api,
    locatesInst as lapi,
    rxInst as rxapi,
    tradingInst as tapi,
    workspaceInst as workspaceApi,
    workspaceRxInst as workspaceRxapi,
};
